<template>
  <div v-bind:class="{ minimized: minimized}">
    <v-list dense dark color="primary-900">
      <div>
        <!-- Manage -->
        <v-subheader class="text-subtitle-1 subheader">{{ $t('manage') }}</v-subheader>
        <v-divider color="grey" class="subheader-divider mx-2 my-5"/>
        <!-- Routes -->
        <v-list-item to="/routes" v-if="user.can('view_route')">
          <v-list-item-icon>
            <v-icon color="white">mdi-monitor-dashboard</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('route_dashboard') }}</v-list-item-content>
        </v-list-item>
        <!-- Root Causes -->
        <v-list-item to="/root-causes" v-if="user.can('view_routeassessment') && customerContext.getSetting('enableViolationRootCauses') === 1">
          <v-list-item-icon>
            <v-badge v-if="unassessedRouteCount > 0" :content="unassessedRouteCount" overlap color="danger" offset-x="12" offset-y="12">
              <v-icon color="white">mdi-alert</v-icon>
            </v-badge>
            <v-icon v-else color="white">mdi-alert</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('root_cause_analysis') }}</v-list-item-content>
        </v-list-item>
        <!-- Route Plans -->
        <v-list-item to="/route-plans" v-if="user.can('view_routeplan') && customerContext.getSetting('routePlanFeedEnabled')">
          <v-list-item-icon>
            <v-icon color="white">mdi-notebook</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('route_plan', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Vehicles -->
        <v-list-item to="/vehicles" v-if="user.can('view_vehicle')">
          <v-list-item-icon>
            <v-icon color="white">mdi-truck</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('vehicle', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Locations -->
        <v-list-item to="/locations" v-if="user.canAny(['create_location', 'update_location', 'delete_location'])">
          <v-list-item-icon>
            <v-badge v-if="unregisteredLocationCount > 0" :content="unregisteredLocationCount" overlap color="danger" offset-x="10" offset-y="10">
              <v-icon color="white">mdi-map-marker</v-icon>
            </v-badge>
            <v-icon v-else color="white">mdi-map-marker</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('location', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Daily Start Temps -->
        <v-list-item to="/product/start-temps" v-if="user.can('view_productstarttemp') && customerContext.getSetting('enableDailyStartTemp') === 1">
          <v-list-item-icon>
            <v-icon color="white">mdi-thermometer-lines</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('product_start_temps') }}</v-list-item-content>
        </v-list-item>
      </div>
      <div>
        <!-- Reporting -->
        <v-subheader class="text-subtitle-1 subheader">{{ $t('reporting') }}</v-subheader>
        <v-divider color="grey" class="subheader-divider mx-2 my-5"/>
        <!-- Fleet Health -->
        <v-list-item to="/fleet-health" v-if="hasFleetHealth">
          <v-list-item-icon>
            <v-badge v-if="customerInsightsNotifications > 0" :content="customerInsightsNotifications" overlap color="danger" offset-x="10" offset-y="10">
              <v-icon color="white">mdi-truck-check</v-icon>
            </v-badge>
            <v-icon v-else color="white">mdi-truck-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">
            {{ $t('fleet_health') }}
          </v-list-item-content>
        </v-list-item>
        <!-- Performance Dashboard -->
        <v-list-item to="/performance/dashboard" v-if="user.can('view_performancedashboard')">
          <v-list-item-icon>
            <v-icon color="white">mdi-chart-pie</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('performance') }}</v-list-item-content>
        </v-list-item>
        <!-- Route Archive -->
        <v-list-item to="/route/list" v-if="user.can('view_route')">
          <v-list-item-icon>
            <v-icon color="white">mdi-archive</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('route_archive') }}</v-list-item-content>
        </v-list-item>
        <!-- Root Cause Archive -->
        <v-list-item to="/root-cause/index" v-if="user.can('view_routeassessment') && customerContext.getSetting('enableViolationRootCauses') === 1">
          <v-list-item-icon>
            <v-icon color="white">mdi-archive-alert</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('root_cause_archive') }}</v-list-item-content>
        </v-list-item>
        <!-- Reports -->
        <v-list-item to="/report/index" v-if="user.can('view_report')">
          <v-list-item-icon>
            <v-icon color="white">mdi-chart-areaspline</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('report', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Audit Logs -->
        <v-list-item to="/audit-logs" v-if="user.can('view_auditlog')">
          <v-list-item-icon>
            <v-icon color="white">mdi-radar</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('audit_log', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Sustainability -->
        <v-list-item to="/sustainability" v-if="user.can('view_sustainability')">
          <v-list-item-icon>
            <v-icon color="white">mdi-leaf</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('sustainability') }}</v-list-item-content>
        </v-list-item>
      </div>
      <div>
        <!-- User -->
        <v-subheader class="text-subtitle-1 subheader">{{ $tc('settings', 2) }}</v-subheader>
        <v-divider color="grey" class="subheader-divider mx-2 my-5"/>
        <!-- Notifications -->
        <v-list-item to="/user/notifications" v-if="user.can('view_notificationrule')">
          <v-list-item-icon>
            <v-icon color="white">mdi-bell</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('notification', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Support -->
        <v-list-item @click="launchZendesk">
          <v-list-item-icon>
            <v-icon color="white">mdi-lifebuoy</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('support') }}</v-list-item-content>
        </v-list-item>
      </div>
      <div v-if="user.can('view_apidocumentation')">
        <!-- Developer -->
        <v-subheader class="text-subtitle-1 subheader">{{ $t('developer') }}</v-subheader>
        <v-divider color="grey" class="subheader-divider mx-2 my-5"/>
        <v-list-item @click="launchApiDocs">
          <v-list-item-icon>
            <v-icon color="white">mdi-code-json</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('api_documentation') }}</v-list-item-content>
        </v-list-item>
      </div>
      <!-- If a root user / verofresh superuser, just show everything without checking permissions -->
      <div v-if="hasRootRole && user.can('view_rootentity')">
        <v-subheader class="text-subtitle-1 subheader">{{ $t('Admin') }}</v-subheader>
        <v-divider color="grey" class="subheader-divider mx-2 my-5"/>
        <!-- Users -->
        <v-list-item to="/users">
          <v-list-item-icon>
            <v-icon color="white">mdi-account-group</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('user', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Customers -->
        <v-list-item to="/customers">
          <v-list-item-icon>
            <v-icon color="white">mdi-office-building</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('customer', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Invoices -->
        <v-list-item to="/invoices">
          <v-list-item-icon>
            <v-icon color="white">mdi-receipt</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('invoice', 2) }}</v-list-item-content>
        </v-list-item>
          <!-- Products -->
        <v-list-item to="/products">
          <v-list-item-icon>
            <v-icon color="white">mdi-silverware-fork-knife</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('product', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- Product Curve Match -->
        <v-list-item to="/product/curve-match">
          <v-list-item-icon>
            <v-icon color="white">mdi-chart-line</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('product_curve_match') }}</v-list-item-content>
        </v-list-item>
        <!-- Banners -->
        <v-list-item to="/banners">
          <v-list-item-icon>
            <v-icon color="white">mdi-alert</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('banner', 2) }}</v-list-item-content>
        </v-list-item>
        <v-list-item to="/energy-price-sets">
          <v-list-item-icon>
            <v-icon color="white">mdi-currency-usd</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('energy_price_set', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- API Requests -->
        <v-list-item to="/api-requests">
          <v-list-item-icon>
            <v-icon color="white">mdi-api</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('api_request', 2) }}</v-list-item-content>
        </v-list-item>
        <!-- API Usage -->
        <v-list-item to="/api-usage">
          <v-list-item-icon>
            <v-icon color="white">mdi-chart-timeline-variant</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $t('api_usage') }}</v-list-item-content>
        </v-list-item>
      </div>
      <!-- Otherwise user might be an admin, in which case we need to show a limited number of admin items -->
      <div v-else-if="hasAdminRole">
        <v-subheader class="text-subtitle-1 subheader">{{ $t('Admin') }}</v-subheader>
        <v-divider color="grey" class="subheader-divider mx-2 my-5"/>
        <!-- Users -->
        <v-list-item to="/users" v-if="user.canAny(['create_user', 'update_user', 'delete_user'], 'all')">
          <v-list-item-icon>
            <v-icon color="white">mdi-account-group</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="text-truncate">{{ $tc('user', 2) }}</v-list-item-content>
        </v-list-item>
      </div>
    </v-list>
  </div>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import AuthUtil from '@/util/AuthUtil'
import UrlHelper from '@/util/Url'
import { some } from 'lodash'

export default {
  name: 'SideNav',
  mixins: [
    CurrentUserMixin
  ],
  data () {
    return {
      minimized: false,
      customerInsightsNotifications: 0
    }
  },
  computed: {
    unregisteredLocationCount () {
      return this.$store.getters['sideNavStateModule/unregisteredLocationCount']
    },
    unassessedRouteCount () {
      return this.$store.getters['sideNavStateModule/unassessedRouteCount']
    },
    hasAdminRole () {
      return some(this.user.roles, x => x.code === 'admin')
    },
    hasRootRole () {
      return some(this.user.roles, x => x.code === 'root')
    },
    /**
     * Return true if the customer has fleet health enabled and the customer can see it.
     * @returns {*|boolean}
     */
    hasFleetHealth () {
      return this.user.can('view_vehicle') && this.customerContext.getSetting('fleetHealth', {}).enabled === 1
    }
  },
  methods: {
    /**
     * Check for Customer Insights notifications (if applicable)
     */
    async fetchCustomerInsightsNotifications () {
      if (this.hasFleetHealth) {
        try {
          const resp = await this.$httpAdapter.get('/customer-insights/notification-count', {
            params: {
              customer_id: this.customerContext.id
            }
          })
          this.customerInsightsNotifications = resp.data
        } catch (error) {
          this.handleError(error)
        }
      }
    },
    /**
     * Refresh unassessed route count (via vuex module)
     */
    async fetchUnassessedRouteCount () {
      if (this.user.can('view_routeassessment') && this.customerContext.getSetting('enableViolationRootCauses') === 1) {
        try {
          await this.$store.dispatch('sideNavStateModule/refreshUnassessedRouteCount', this.customerContextId)
        } catch (error) {
          this.handleError(error)
        }
      }
    },
    /**
     * Refresh unregistered location count (via vuex module)
     */
    async fetchUnregisteredLocationCount () {
      if (this.user.canAny(['create_location', 'update_location', 'delete_location'])) {
        try {
          await this.$store.dispatch('sideNavStateModule/refreshUnregisteredLocationCount', this.customerContextId)
        } catch (error) {
          this.handleError(error)
        }
      }
    },
    /**
     * Toggle the minimized nav on or off.
     */
    toggleMinimizeNav () {
      this.minimized = !this.minimized
      this.$store.commit('userModule/setUseMiniSideNav', this.minimized)
    },
    /**
     * Launch Zendesk. If user is root, take straight to the zendesk agent login. Otherwise, obtain an SSO token from
     * the API then redirect to Zendesk.
     */
    async launchZendesk () {
      if (this.user.can('view_rootentity')) {
        window.open(`https://${process.env.VUE_APP_ZENDESK_DOMAIN}/agent`, '_blank')
        return
      }
      try {
        const resp = await this.$httpAdapter.get('/auth/zendesk-sso')
        const jwt = resp.data
        window.open(`https://${process.env.VUE_APP_ZENDESK_SSO_DOMAIN}/access/jwt?jwt=${jwt}`, '_blank')
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Launch the api docs in a separate window.
     */
    launchApiDocs () {
      window.open(`${UrlHelper.getBaseApiDocsUrl()}/?bearer=${AuthUtil.getJwt()}`, '_blank')
    },
    customerContextChangeHandler (newCustomer, oldCustomer) {
      this.fetchCustomerInsightsNotifications()
      this.fetchUnassessedRouteCount()
      this.fetchUnregisteredLocationCount()
    }
  },
  created () {
    this.minimized = this.$store.getters['userModule/useMiniSideNav']
  },
  mounted () {
    this.fetchCustomerInsightsNotifications()
    this.fetchUnassessedRouteCount()
    this.fetchUnregisteredLocationCount()
  }
}
</script>

<style lang="scss">
  .v-divider.subheader-divider {
    display: none;
  }
  // CSS to make the minimized mode take effect. This only works in Vuetify breakpoints 'md' and up, so as not to
  // overlap with the slider configuration that is used for the smaller breakpoints.
  @media screen and (min-width: 961px) {
    .minimized {
      .subheader {
        display: none;
      }
      .v-divider.subheader-divider {
        display: block;
      }
      .v-list-item__content {
        display: none;
      }
      .v-list-item__icon:first-child {
        margin-right: 0 !important;
      }
    }
  }
</style>
